import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import './SurveyPage.css';

const ALLOWED_FIELDS = new Set([
    'school_level',
  'gender',
  'gender_self_describe',
  'university',
  'major',
  'race_ethnicity',
  'cs_courses',
      'llm_familiarity',
  'confidence_greedy_dp',
  'confidence_leetcode',
  'confidence_nl_description',
]);

const DEFAULT_FORM = {
  school_level: '',
  gender: '',
  gender_self_describe: '',
  university: '',
  major: '',
  race_ethnicity: [],   // checkbox group
  cs_courses: [],       // checkbox group
  llm_familiarity: '',
  confidence_greedy_dp: '',
  confidence_leetcode: '',
  confidence_nl_description: '',
};

function SurveyPage() {
  const navigate = useNavigate();
  useEffect(() => {
        try {
            const v = JSON.parse(localStorage.getItem('rt_survey_submitted') || 'null');
        if (v && v.ts) {
          navigate('/', { replace: true }); // or '/ide' etc.
        }
      } catch {}
    }, [navigate]);

  // hydrate page
  const [page, setPage] = useState(() => {
    const raw = localStorage.getItem('rt_survey_page');
    const n = Number(raw);
    return Number.isFinite(n) && n >= 1 ? n : 1;
  });

  // hydrate formData with defaults + saved
  const [formData, setFormData] = useState(() => {
    try {
      const saved = JSON.parse(localStorage.getItem('rt_survey_draft') || '{}');
      if (saved && typeof saved === 'object') {
               for (const k of Object.keys(saved)) {
                 if (!ALLOWED_FIELDS.has(k)) delete saved[k];
               }
             }
      return {
        ...DEFAULT_FORM,
        ...saved,
        race_ethnicity: Array.isArray(saved?.race_ethnicity) ? saved.race_ethnicity : [],
        cs_courses: Array.isArray(saved?.cs_courses) ? saved.cs_courses : [],
      };
    } catch {
      return { ...DEFAULT_FORM };
    }
  });

  useEffect(() => {
    try {
      localStorage.setItem('rt_flow_stage', 'survey');
      localStorage.setItem('rt_last_route', '/survey');
    } catch {}
  }, []);


  // autosave draft + page
  useEffect(() => {
    try { localStorage.setItem('rt_survey_draft', JSON.stringify(formData)); } catch {}
  }, [formData]);

  useEffect(() => {
    try { localStorage.setItem('rt_survey_page', String(page)); } catch {}
  }, [page]);

  const [error, setError] = useState('');

  // text/radio inputs
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  // checkbox groups
  const handleCheckboxChange = (e) => {
    const { name, value, checked } = e.target;
    setFormData(prev => {
      const current = Array.isArray(prev[name]) ? prev[name] : [];
      const next = checked
        ? [...new Set([...current, value])]
        : current.filter(v => v !== value);
      return { ...prev, [name]: next };
    });
  };

  // validate page 1, go to page 2
  const handleNextPage = () => {
    const demographicFields = ['school_level', 'gender', 'university', 'major'];
    for (const field of demographicFields) {
      if (!formData[field] || String(formData[field]).trim() === '') {
        setError('Please fill out all required demographic questions before proceeding.');
        return;
      }
    }
    if (!Array.isArray(formData.race_ethnicity) || formData.race_ethnicity.length === 0) {
      setError('Please select an option for race/ethnicity.');
      return;
    }
    if (formData.gender === 'Prefer to self-describe' && !formData.gender_self_describe.trim()) {
      setError('Please describe your gender.');
      return;
    }

    setError('');
    setPage(2);
  };

  const handleSubmit = async (e) => {
    if (e) e.preventDefault();
    setError('');
  
    // --- Basic required checks across all fields you present ---
    for (const [k, v] of Object.entries(formData)) {
      if (Array.isArray(v) && v.length === 0) {
        setError('Please answer all required questions.');
        return;
      }
      if (typeof v === 'string' && v.trim() === '' && k !== 'gender_self_describe') {
        setError('Please answer all required questions.');
        return;
      }
    }
  
    try {
      const resp = await fetch('/api/submit_survey', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',              // keep the session cookie
        body: JSON.stringify(formData),
      });
  
      if (!resp.ok) {
        setError('Failed to submit survey. Please try again.');
        return;
      }
  
      // --- Persist a submitted snapshot & advance flow to Steps ---
      try {
        localStorage.setItem(
          'rt_survey_submitted',
          JSON.stringify({ ts: new Date().toISOString(), answers: formData })
        );
        // Clear any in-progress state for a clean re-open
        localStorage.removeItem('rt_survey_draft');
        localStorage.removeItem('rt_survey_page');
  
        // Move the app to the Steps stage and remember a safe route
        localStorage.setItem('rt_flow_stage', 'example');
        localStorage.setItem('rt_last_route', '/');
      } catch {
        // ignore storage errors
      }
  
      navigate('/', { replace: true });      // go to Steps
    } catch {
      setError('An error occurred. Please try again.');
    }
  };
  
  return (
    <div className="survey-container">
      <div className="survey-form-wrapper">
        <div className="survey-header">
          <h1>Background Survey</h1>
          <p>Thank you for participating. Please complete this survey.</p>
        </div>

        <form onSubmit={handleSubmit} className="survey-form">
          {/* --- Page 1: Demographic Questions --- */}
          {page === 1 && (
            <div className="survey-section">
              <h2>Demographic Questions</h2>

              <div className="survey-question">
                <label>What is your current level in school? *</label>
                <div className="radio-group">
                  {['Freshman', 'Sophomore', 'Junior', 'Senior', 'Graduate Student', 'Other'].map(option => (
                    <label key={option}>
                      <input
                        type="radio"
                        name="school_level"
                        value={option}
                        onChange={handleInputChange}
                        checked={formData.school_level === option}
                        required
                      />
                      {option}
                    </label>
                  ))}
                </div>
              </div>

              <div className="survey-question">
                <label>Which university do you attend? *</label>
                <input
                  type="text"
                  name="university"
                  value={formData.university}
                  onChange={handleInputChange}
                  spellCheck={false}
                  required
                />
              </div>

              <div className="survey-question">
                <label>What is your major? *</label>
                <input
                  type="text"
                  name="major"
                  value={formData.major}
                  onChange={handleInputChange}
                  spellCheck={false}
                  required
                />
              </div>

              <div className="survey-question">
                <label>What is your gender? *</label>
                <div className="radio-group">
                  {['Man', 'Woman', 'Non-binary', 'Prefer not to disclose', 'Prefer to self-describe'].map(option => (
                    <label key={option}>
                      <input
                        type="radio"
                        name="gender"
                        value={option}
                        onChange={handleInputChange}
                        checked={formData.gender === option}
                        required
                      />
                      {option}
                    </label>
                  ))}
                </div>

                {formData.gender === 'Prefer to self-describe' && (
                  <input
                    type="text"
                    name="gender_self_describe"
                    value={formData.gender_self_describe}
                    onChange={handleInputChange}
                    placeholder="Please describe"
                    spellCheck={false}
                    style={{ marginTop: '0.5rem', padding: '0.5rem', width: '100%' }}
                  />
                )}
              </div>

              <div className="survey-question">
                <label>What is your racial or ethnic identification? (You may select multiple answers) *</label>
                <div className="checkbox-group">
                  {[
                    'American Indian or Alaska Native',
                    'Asian',
                    'Black or African American',
                    'Hispanic or Latino',
                    'Native Hawaiian or Pacific Islander',
                    'White',
                    'Multi-racial',
                    'Prefer not to disclose',
                    'Other',
                  ].map(option => (
                    <label key={option}>
                      <input
                        type="checkbox"
                        name="race_ethnicity"
                        value={option}
                        checked={formData.race_ethnicity.includes(option)}
                        onChange={handleCheckboxChange}
                      />
                      {option}
                    </label>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* --- Page 2: Experience Questions --- */}
          {page === 2 && (
            <div className="survey-section">
              <h2>Experience Questions</h2>

              <div className="survey-question">
                <label>What CS or CS-related courses have you taken? (You may select multiple answers) *</label>
                <div className="checkbox-group">
                  {['None', 'Introduction to Computer Science', 'Data Structures', 'Systems Programming', 'Algorithms', 'Statistical Programming', 'Other'].map(option => (
                    <label key={option}>
                      <input
                        type="checkbox"
                        name="cs_courses"
                        value={option}
                        checked={formData.cs_courses.includes(option)}
                        onChange={handleCheckboxChange}
                      />
                      {option}
                    </label>
                  ))}
                </div>
              </div>

              <div className="survey-question">
                <label>How familiar are you with large language models (LLMs), such as ChatGPT, Claude, or Gemini? *</label>
                <div className="radio-group">
                  {[
                    "Not familiar - I've never heard of them before",
                    "Slightly familiar - I've heard of them but haven't used one",
                    "Somewhat familiar - I've used them a few times casually",
                    "Familiar - I use them regularly for basic tasks",
                    "Very familiar - I use them often and understand how they work",
                  ].map(option => (
                    <label key={option}>
                      <input
                        type="radio"
                        name="llm_familiarity"
                        value={option}
                        onChange={handleInputChange}
                        checked={formData.llm_familiarity === option}
                        required
                      />
                      {option}
                    </label>
                  ))}
                </div>
              </div>

              <div className="survey-question">
                <label>How would you rate your understanding of greedy algorithms? *</label>
                <div className="radio-group">
                  {['Not at all confident', 'Slightly confident', 'Moderately confident', 'Confident', 'Very confident'].map(option => (
                    <label key={option}>
                      <input
                        type="radio"
                        name="confidence_greedy_dp"
                        value={option}
                        onChange={handleInputChange}
                        checked={formData.confidence_greedy_dp === option}
                        required
                      />
                      {option}
                    </label>
                  ))}
                </div>
              </div>

              <div className="survey-question" style={{ background: '#f9fafb', padding: '1rem', borderRadius: '8px' }}>
                <p>Please read the following problem on LeetCode and answer the following questions with this problem in mind:</p>
                <a
                  href="https://leetcode.com/problems/gas-station/description/"
                  target="_blank"
                  rel="noopener noreferrer"
                  style={{ color: '#7c3aed', fontWeight: 600 }}
                >
                  Gas Station (LeetCode Problem)
                </a>
              </div>

              <div className="survey-question">
                <label>How confident are you in solving such algorithmic programming problems on Leetcode? *</label>
                <div className="radio-group">
                  {['Not at all confident', 'Slightly confident', 'Moderately confident', 'Confident', 'Very confident'].map(option => (
                    <label key={option}>
                      <input
                        type="radio"
                        name="confidence_leetcode"
                        value={option}
                        onChange={handleInputChange}
                        checked={formData.confidence_leetcode === option}
                        required
                      />
                      {option}
                    </label>
                  ))}
                </div>
              </div>

              {/* <div className="survey-question">
                <label>How confident are you in identifying edge cases for a given question? *</label>
                <div className="radio-group">
                  {['Not at all confident', 'Slightly confident', 'Moderately confident', 'Confident', 'Very confident'].map(option => (
                    <label key={option}>
                      <input
                        type="radio"
                        name="confidence_edge_cases"
                        value={option}
                        onChange={handleInputChange}
                        checked={formData.confidence_edge_cases === option}
                        required
                      />
                      {option}
                    </label>
                  ))}
                </div>
              </div> */}

              <div className="survey-question">
                <label>How confident are you in describing your technical approach to solving a problem in natural language? *</label>
                <div className="radio-group">
                  {['Not at all confident', 'Slightly confident', 'Moderately confident', 'Confident', 'Very confident'].map(option => (
                    <label key={option}>
                      <input
                        type="radio"
                        name="confidence_nl_description"
                        value={option}
                        onChange={handleInputChange}
                        checked={formData.confidence_nl_description === option}
                        required
                      />
                      {option}
                    </label>
                  ))}
                </div>
              </div>
            </div>
          )}

          {error && <p className="survey-error">{error}</p>}

          {/* --- Navigation Buttons --- */}
          <div className="survey-nav">
            {page === 2 && (
              <button type="button" onClick={() => setPage(1)} className="survey-back-btn">
                Back
              </button>
            )}
            {page === 1 ? (
              <button type="button" onClick={handleNextPage} className="survey-next-btn">
                Next
              </button>
            ) : (
              <button type="submit" className="survey-submit-btn">
                Submit
              </button>
            )}
          </div>
        </form>
      </div>
    </div>
  );
}

export default SurveyPage;
